import React, { useCallback, useState } from "react";
import { ChevronRight } from "lucide-react";
import { BasePage } from "../BasePage";
import { useNavigate } from "react-router";
import { useRootStore } from "@/state/RootStore";
import moment from "moment";
import { KNOWLEDGE_LEVELS } from "./CreateProfile";
import { useClerk } from "@clerk/clerk-react";
import { observer } from "mobx-react";
import ExamBookingSection from "./ExamBookingSection";
import { useTranslation } from "react-i18next";
import { showToastFromKey } from "@/lib/toast";
import { useIsMobileWeb } from "@/hooks/useIsMobileWeb";
import DownloadAppCta, {
  DOWNLOAD_APP_CTA_CLEARANCE_CLASS,
} from "@/components/mobile/DownloadAppCta";

/**
 * EditProfileInfoRefactored component
 *
 * A form that allows users to edit their profile information including name, email,
 * school, exam date, and level. The email field is disabled and requires contacting support to change.
 */
const EditProfileInfoRefactored = observer(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const store = useRootStore();
  const { user } = useClerk();
  const isMobileWeb = useIsMobileWeb();

  const [name, setName] = useState<string>(
    store.mainAppStore.profileName || ""
  );
  const [school, setSchool] = useState<string>(
    store.mainAppStore.profileSchool || ""
  );
  const [hasBookedExam, setHasBookedExam] = useState<boolean | null>(
    store.mainAppStore.hasBookedExam ?? null
  );
  const [examDateRaw, setExamDateRaw] = useState<string>(() => {
    if (store.mainAppStore.hasBookedExam === true && store.mainAppStore.scheduledExamDate) {
      return moment(store.mainAppStore.scheduledExamDate, "MMMM D, YYYY").format("YYYY-MM-DD");
    }
    if (store.mainAppStore.examDateSelection) {
      return moment(store.mainAppStore.examDateSelection, ["MMMM YYYY", "MMMM, YYYY"]).format("YYYY-MM");
    }
    return "";
  });
  const [level, setLevel] = useState<string>(
    store.mainAppStore.knowledgeLevel || ""
  );
  const [email, _] = useState<string>(store.mainAppStore.profileEmail || "");
  const [isSaving, setIsSaving] = useState(false);

  const handleDeleteProfile = useCallback(async () => {
    await store.mainAppStore.resetProfile();
    navigate("/profile");
  }, []);

  const handleSaveProfile = useCallback(async () => {
    if (!examDateRaw) {
      showToastFromKey({
        titleKey: "errors.generic_title",
        descriptionKey:
          hasBookedExam === true
            ? "profileCreate.validation_select_exam_date"
            : "profileCreate.validation_select_estimated_month",
        variant: "destructive",
      });
      return;
    }
    setIsSaving(true);
    try {
      store.mainAppStore.setProfileName(name);
      store.mainAppStore.setProfileSchool(school);
      store.mainAppStore.setHasBookedExam(hasBookedExam);
      if (hasBookedExam === true && examDateRaw) {
        store.mainAppStore.setScheduledExamDate(
          moment(examDateRaw, "YYYY-MM-DD").format("MMMM D, YYYY")
        );
      } else if (hasBookedExam === false && examDateRaw) {
        store.mainAppStore.setExamDateSelection(
          moment(examDateRaw, "YYYY-MM").format("MMMM YYYY")
        );
      }
      store.mainAppStore.setKnowledgeLevel(level);

      // The setters above have ALREADY mutated the in-memory profile, so navigating on a
      // failed save shows the edited values on /profile as though they persisted — and
      // they silently revert on the next reload. `saveProfile` toasts the failure, so
      // staying put is both honest and recoverable. CreateProfile already checks this.
      const saved = await store.mainAppStore.saveProfile();
      if (!saved) return;

      navigate("/profile");
    } finally {
      setIsSaving(false);
    }
  }, [name, school, hasBookedExam, examDateRaw, level]);

  const content = (
    <>
      <div
        className={`grow flex flex-col items-center ${
          isMobileWeb
            ? `px-6 py-8 ${DOWNLOAD_APP_CTA_CLEARANCE_CLASS}`
            : "min-h-full px-4 pt-8 sm:px-8 md:pt-16"
        }`}
      >
        <div className="flex w-full max-w-[594px] flex-col gap-[34px]">
          {/* Breadcrumb Navigation */}
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => navigate("/profile")}
              className="text-zinc-500 text-sm font-normal font-inter hover:underline"
            >
              {t("profile.breadcrumb")}
            </button>
            <ChevronRight className="w-4 h-4 text-zinc-500" />
            <span className="text-zinc-950 text-sm font-normal font-inter">
              {t("profile.edit_profile_info")}
            </span>
          </div>

          {/* Form Fields */}
          <div className="flex flex-col gap-[34px]">
            <div className="flex flex-col gap-4">
              {/* Name Field */}
              <div className="flex flex-col gap-1.5">
                <label className="text-zinc-950 text-sm font-medium font-inter" htmlFor="edit-profile-name">
                  {t("profile.name_label")}
                </label>
                <input
                  id="edit-profile-name"
                  type="text"
                  defaultValue={name}
                  onChange={(e) => setName(e.target.value)}
                  className="h-10 px-3 py-2 bg-white rounded-md border border-[#efefef] text-black text-sm font-normal font-inter"
                />
              </div>

              {/* Authentication ID */}
              <div className="flex flex-col gap-2">
                <div className="flex flex-col gap-1.5">
                  <label className="text-zinc-950 text-sm font-medium font-inter" htmlFor="edit-profile-auth-id">
                    {t("profile.auth_id_label")}
                  </label>
                  <input
                    id="edit-profile-auth-id"
                    type="email"
                    defaultValue={user?.emailAddresses[0].emailAddress}
                    disabled
                    className="h-10 px-3 py-2 bg-[#ebecf0] rounded-md border border-[#efefef] text-black text-sm font-normal font-inter"
                  />
                </div>
              </div>

              {/* Email Field */}
              <div className="flex flex-col gap-2">
                <div className="flex flex-col gap-1.5">
                  <label className="text-zinc-950 text-sm font-medium font-inter" htmlFor="edit-profile-email">
                    {t("profile.email_label")}
                  </label>
                  <input
                    id="edit-profile-email"
                    type="email"
                    defaultValue={email}
                    disabled
                    className="h-10 px-3 py-2 bg-[#ebecf0] rounded-md border border-[#efefef] text-black text-sm font-normal font-inter"
                  />
                </div>
                <span className="text-[#666666] text-[10px] font-normal font-inter">
                  {t("profile.email_change_helper")}
                </span>
              </div>

              {/* School Field */}
              <div className="flex flex-col gap-1.5">
                <label className="text-zinc-950 text-sm font-medium font-inter" htmlFor="edit-profile-school">
                  {t("profile.school_label")}
                </label>
                <input
                  id="edit-profile-school"
                  type="text"
                  defaultValue={school}
                  onChange={(e) => setSchool(e.target.value)}
                  className="h-10 px-3 py-2 bg-white rounded-md border border-[#efefef] text-black text-sm font-normal font-inter"
                />
              </div>

              {/* Exam Booking Section */}
              <ExamBookingSection
                hasBookedExam={hasBookedExam}
                onBookedChange={(value) => {
                  setHasBookedExam(value);
                  setExamDateRaw("");
                }}
                dateInputValue={examDateRaw}
                onDateChange={setExamDateRaw}
                minDate={moment().format("YYYY-MM-DD")}
                maxDate={moment().add(5, "years").format("YYYY-MM-DD")}
                minMonth={moment().format("YYYY-MM")}
                maxMonth={moment().add(5, "years").format("YYYY-MM")}
                variant="edit"
              />

              {/* Your Level Field */}
              <div className="flex flex-col gap-1.5">
                <label className="text-zinc-950 text-sm font-medium font-inter" htmlFor="edit-profile-level">
                  {t("profile.your_level_label")}
                </label>
                <select
                  id="edit-profile-level"
                  className="h-10 px-3 py-2 bg-white rounded-md border border-[#efefef] text-sm font-normal font-inter appearance-none"
                  defaultValue={level}
                  onChange={(e) => setLevel(e.target.value)}
                  required
                >
                  <option value="" disabled>
                    {t("profile.level_select_placeholder")}
                  </option>
                  {KNOWLEDGE_LEVELS.map((level) => (
                    <option key={level.value} value={level.value}>
                      {t(level.labelKey)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col gap-3">
              <button
                onClick={handleSaveProfile}
                disabled={isSaving}
                className="dp-button-primary mb-8 h-[51px] px-8 py-4 text-center text-base font-inter disabled:opacity-50"
              >
                {isSaving ? t("profile.saving_button") : t("common.save_changes")}
              </button>
            </div>
          </div>
        </div>
      </div>
      {isMobileWeb && <DownloadAppCta />}
    </>
  );

  if (isMobileWeb) {
    return <div className="flex min-h-full w-full bg-white">{content}</div>;
  }

  return (
    <BasePage activeItem="home" contentClassName="md:pt-0">
      <div className="min-h-full bg-white">{content}</div>
    </BasePage>
  );
});

export default EditProfileInfoRefactored;
